import React, { useEffect, useState } from "react";
import { api, formatApiError, imageUrl } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import SortableItem from "../components/SortableItem";
import ConfirmDialog from "../components/ConfirmDialog";
import useToast from "../hooks/use-toast-admin";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Plus, Pencil, Trash, X, Check } from "@phosphor-icons/react";

const emptyForm = {
  title: "",
  subtitle: "",
  cta_label: "",
  cta_link: "",
  image_path: "",
  order: 0,
  active: true,
};

export default function AdminSlidesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slide or "new"
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null); // { s, busy, err }
  const { showToast, ToastView } = useToast();
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/slides")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...emptyForm, order: items.length });
    setErr("");
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title || "",
      subtitle: s.subtitle || "",
      cta_label: s.cta_label || "",
      cta_link: s.cta_link || "",
      image_path: s.image_path || "",
      order: s.order || 0,
      active: !!s.active,
    });
    setErr("");
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm);
    setErr("");
  };

  const save = async () => {
    if (!form.title || !form.image_path) {
      setErr("Title and image are required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      if (editing === "new") {
        await api.post("/slides", form);
      } else {
        await api.put(`/slides/${editing.id}`, form);
      }
      await load();
      close();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = (s) => setConfirmTarget({ s, busy: false, err: "" });

  const performDelete = async () => {
    if (!confirmTarget?.s) return;
    setConfirmTarget((c) => ({ ...c, busy: true, err: "" }));
    try {
      await api.delete(`/slides/${confirmTarget.s.id}`);
      const removed = confirmTarget.s;
      setConfirmTarget(null);
      setItems((prev) => prev.filter((x) => x.id !== removed.id));
      showToast("ok", `Deleted slide "${removed.title}".`);
      await load();
    } catch (e) {
      setConfirmTarget((c) => ({ ...c, busy: false, err: formatApiError(e) }));
    }
  };

  const toggle = async (s) => {
    await api.put(`/slides/${s.id}`, { active: !s.active });
    await load();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex).map((it, idx) => ({ ...it, order: idx }));
    setItems(next); // optimistic
    try {
      await api.post("/slides/reorder", { ids: next.map((n) => n.id) });
    } catch {
      load();
    }
  };

  return (
    <div className="p-10" data-testid="admin-slides-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline text-[#a8b2cc] mb-3">Homepage Slideshow</div>
          <h1 className="font-display text-5xl tracking-tighter">Slides</h1>
          <p className="mt-3 text-[#a8b2cc] max-w-xl">
            Upload and manage the hero slideshow on the homepage. Active slides appear
            in order of the "order" field.
          </p>
        </div>
        <button onClick={openNew} className="btn-brutalist" data-testid="add-slide-button">
          <Plus size={16} weight="bold" /> New Slide
        </button>
      </div>

      {loading ? (
        <div className="text-[#a8b2cc] font-mono-accent">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#2c3854] bg-[#141e32] p-16 text-center">
          <div className="font-display text-3xl mb-3">No slides yet</div>
          <p className="text-[#a8b2cc] mb-6">Add your first homepage slide.</p>
          <button onClick={openNew} className="btn-brutalist" data-testid="empty-add-slide">
            <Plus size={16} weight="bold" /> New Slide
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 font-mono-accent text-[0.72rem] uppercase tracking-[0.22em] text-[#a8b2cc]">
            Drag the handle on each card to reorder
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((s) => s.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {items.map((s) => (
                  <SortableItem key={s.id} id={s.id} testIdPrefix="slide">
                    <article
                      className="sharp-card flex flex-col"
                      data-testid={`slide-card-${s.id}`}
                    >
                      {s.image_path && (
                        <img src={imageUrl(s.image_path)} alt={s.title} className="w-full aspect-[16/10] object-cover border-b border-[#2c3854]" />
                      )}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <span className="overline text-[#a8b2cc]">ORDER {s.order}</span>
                          <button
                            onClick={() => toggle(s)}
                            className={`overline px-3 py-1 border ${
                              s.active
                                ? "bg-[#141e32] text-white border-[#f5ecd9]"
                                : "bg-[#141e32] text-[#a8b2cc] border-[#2c3854]"
                            }`}
                            data-testid={`slide-toggle-${s.id}`}
                          >
                            {s.active ? "LIVE" : "DRAFT"}
                          </button>
                        </div>
                        <h3 className="font-display text-xl tracking-tight">{s.title}</h3>
                        {s.subtitle && <p className="text-sm text-[#a8b2cc] mt-2 line-clamp-2">{s.subtitle}</p>}
                        <div className="flex gap-2 mt-auto pt-4">
                          <button
                            onClick={() => openEdit(s)}
                            className="btn-ghost flex-1 justify-center"
                            data-testid={`slide-edit-${s.id}`}
                          >
                            <Pencil size={14} weight="bold" /> Edit
                          </button>
                          <button
                            onClick={() => remove(s)}
                            className="border border-[#af3fa6] text-[#af3fa6] hover:bg-[#af3fa6] hover:text-white p-3 transition-colors"
                            data-testid={`slide-delete-${s.id}`}
                            aria-label="Delete slide"
                          >
                            <Trash size={16} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </article>
                  </SortableItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <div
            className="bg-[#141e32] border border-[#f5ecd9] w-full max-w-3xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="slide-editor-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#2c3854]">
              <div>
                <div className="overline text-[#a8b2cc] mb-1">{editing === "new" ? "New" : "Edit"} Slide</div>
                <h2 className="font-display text-3xl tracking-tighter">
                  {editing === "new" ? "Create slide" : form.title}
                </h2>
              </div>
              <button onClick={close} className="p-2 hover:bg-[#070b14]" data-testid="slide-editor-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {err && (
                <div className="border border-[#af3fa6] text-[#af3fa6] p-3 text-sm" data-testid="slide-editor-error">
                  {err}
                </div>
              )}

              <FieldLabel label="Image">
                <ImageUpload
                  value={form.image_path}
                  onChange={(p) => setForm({ ...form, image_path: p })}
                  testid="slide-image-upload"
                />
              </FieldLabel>

              <FieldLabel label="Title">
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="admin-input"
                  data-testid="slide-input-title"
                />
              </FieldLabel>

              <FieldLabel label="Subtitle">
                <textarea
                  rows={2}
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  className="admin-input resize-none"
                  data-testid="slide-input-subtitle"
                />
              </FieldLabel>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FieldLabel label="Button Label">
                  <input
                    value={form.cta_label}
                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                    className="admin-input"
                    data-testid="slide-input-cta-label"
                  />
                </FieldLabel>
                <FieldLabel label="Button Link">
                  <input
                    value={form.cta_link}
                    onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                    placeholder="/offers"
                    className="admin-input"
                    data-testid="slide-input-cta-link"
                  />
                </FieldLabel>
                <FieldLabel label="Order (lowest first)">
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })}
                    className="admin-input"
                    data-testid="slide-input-order"
                  />
                </FieldLabel>
                <FieldLabel label="Status">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      data-testid="slide-input-active"
                    />
                    <span className="font-mono-accent text-[0.8rem] uppercase tracking-[0.18em]">
                      {form.active ? "Live on site" : "Draft"}
                    </span>
                  </label>
                </FieldLabel>
              </div>
            </div>

            <div className="p-6 border-t border-[#2c3854] flex gap-3 justify-end">
              <button onClick={close} className="btn-ghost" data-testid="slide-editor-cancel">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-brutalist disabled:opacity-60"
                data-testid="slide-editor-save"
              >
                <Check size={16} weight="bold" /> {saving ? "Saving…" : "Save slide"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete this slide?"
        description={
          confirmTarget?.s ? (
            <>
              Permanently removes the slide{" "}
              <span className="text-[#f5ecd9]">"{confirmTarget.s.title}"</span> from the
              homepage slideshow. This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Yes, delete slide"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={performDelete}
        busy={confirmTarget?.busy}
        error={confirmTarget?.err}
        testId="slide-confirm-delete"
      />
      <ToastView />
    </div>
  );
}

const FieldLabel = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
      {label}
    </span>
    {children}
  </label>
);
